import type { FC } from "react";
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import appConfig from "config/appConfig";
import { useTranslation } from "react-i18next";

import DollarIcon from "assets/icons/outlined/dollar.svg?react";

import { CardWrapper, PaymentStatBlock } from "shared/Components";
import { useSoccerPayment } from "shared/hooks";

import type { MenuDotsOption } from "shared/uikit";
import { Button, InfoIcon, Text } from "shared/uikit";

import { ButtonSizesEnum, ButtonVariantsEnum, TextVariantsEnum } from "shared/uikit/types";

import { InfoIconType } from "../../uikit/InfoIcon";

const WithdrawMoneyDialog = lazy(() => import("shared/Components/WithdrawMoneyDialog"));
const BusinessCountryModal = lazy(() => import("shared/Components/BusinessCountryModal"));

interface PaymentMetricsProps {
	objectId: string;
	paymentAccountExists?: boolean;
	requestingRegistration?: boolean;
	onRegisterPaymentAccount?: (countryCode: string) => void;
	allowViewStripeDashboard?: boolean;
	stripeAccountValid?: boolean;
}

const PaymentMetrics: FC<PaymentMetricsProps> = ({
	objectId,
	paymentAccountExists,
	requestingRegistration,
	onRegisterPaymentAccount,
	allowViewStripeDashboard,
	stripeAccountValid
}) => {
	const { t } = useTranslation();

	const { getPaymentAccountMetrics, getData: getSoccerPaymentData } = useSoccerPayment();
	const { loadingPaymentAccountMetrics, paymentAccountMetrics } = getSoccerPaymentData();

	const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
	const [showBusinessCountryModal, setShowBusinessCountryModal] = useState(false);

	const fetchData = useCallback(
		(objectId: string) => {
			getPaymentAccountMetrics({ objectId });
		},
		[getPaymentAccountMetrics]
	);

	const registerPaymentAccount = useCallback(() => {
		setShowBusinessCountryModal(true);
	}, []);

	useEffect(() => {
		fetchData(objectId);
	}, [fetchData, objectId]);

	// const balanceOptions: MenuDotsOption[] = useMemo(
	// 	() =>
	// 		paymentAccountMetrics?.balance?.total
	// 			? [{ name: "Withdraw amount", onClick: () => setWithdrawDialogOpen(true) }]
	// 			: [],
	// 	[paymentAccountMetrics?.balance?.total]
	// );

	const balanceOptions: MenuDotsOption[] = useMemo(() => [], []);

	const handleCloseCountryModal = (countryCode?: string) => {
		setShowBusinessCountryModal(false);
		if (countryCode) {
			onRegisterPaymentAccount && onRegisterPaymentAccount(countryCode);
		}
	};

	return (
		<>
			<div className={clsx("grid gap-4", allowViewStripeDashboard ? "grid-cols-4" : "grid-cols-3")}>
				<PaymentStatBlock
					loading={loadingPaymentAccountMetrics}
					title="Cash In"
					currentAmount={paymentAccountMetrics?.inbound?.currentPeriod}
					previousAmount={paymentAccountMetrics?.inbound?.previousPeriod}
					disabled={!paymentAccountExists}
				/>
				<PaymentStatBlock
					loading={loadingPaymentAccountMetrics}
					title="Cash Out"
					currentAmount={paymentAccountMetrics?.outbound?.currentPeriod}
					previousAmount={paymentAccountMetrics?.outbound?.previousPeriod}
					inverted
					disabled={!paymentAccountExists}
				/>
				<PaymentStatBlock
					loading={loadingPaymentAccountMetrics}
					title="Balance"
					currentAmount={paymentAccountMetrics?.balance?.total}
					previousAmount={paymentAccountMetrics?.balance?.previousPeriod}
					opts={balanceOptions}
					disabled={!paymentAccountExists}
				/>
				{!!allowViewStripeDashboard && (
					<CardWrapper
						className={clsx(
							"h-full flex flex-col justify-between",
							paymentAccountExists && !stripeAccountValid && "border-red-500"
						)}
					>
						<div className="flex items-center gap-2">
							<Text variants={TextVariantsEnum.H7} className={"text-primary-600"}>
								{t("payment:payment_account")}
							</Text>
							{paymentAccountExists && !stripeAccountValid && (
								<div className="relative z-[1]">
									<InfoIcon
										text={t("payment:stripe_connect_account_issue")}
										placement={"bottom"}
										type={InfoIconType.WARNING}
									/>
								</div>
							)}
						</div>
						{!paymentAccountExists && !!onRegisterPaymentAccount && (
							<Button
								size={ButtonSizesEnum.SM}
								variant={ButtonVariantsEnum.OUTLINED}
								startIcon={<DollarIcon className="svg-paths:fill-primary-500" />}
								className="w-full text-primary-500"
								onClick={registerPaymentAccount}
								loading={requestingRegistration}
							>
								{t("register")}
							</Button>
						)}
						{paymentAccountExists && (
							<Button
								size={ButtonSizesEnum.SM}
								variant={ButtonVariantsEnum.OUTLINED}
								startIcon={<DollarIcon className="svg-paths:fill-primary-500" />}
								className="w-full text-primary-500"
								link={{
									to: appConfig.GLOBAL_CONSTANTS.STRIPE_CONNECT_ACCOUNT_DASHBOARD,
									target: "_blank"
								}}
							>
								{t("payment:stripe_dashboard")}
							</Button>
						)}
					</CardWrapper>
				)}
			</div>
			{withdrawDialogOpen && !!paymentAccountMetrics?.balance?.total && (
				<Suspense>
					<WithdrawMoneyDialog
						balance={paymentAccountMetrics.balance.total}
						onClose={() => setWithdrawDialogOpen(false)}
					/>
				</Suspense>
			)}
			{showBusinessCountryModal && (
				<Suspense>
					<BusinessCountryModal onClose={handleCloseCountryModal} />
				</Suspense>
			)}
		</>
	);
};

export default PaymentMetrics;
